import { conflictingLighthouse, hasLighthouse, isComplete, territoryConflict } from './rules';
import { beamCounts } from './visibility';

export const createGameState = (puzzle) => ({
  puzzle,
  lighthouses: [],
  strikes: 0,
  history: [],
  future: [],
  previewedLighthouse: null,
  beamFlash: null,
  feedback: null,
  completed: false,
});

const snapshot = (state) => state.lighthouses.map((item) => ({ ...item }));

const withLighthouses = (state, lighthouses, history, future = []) => ({
  ...state,
  lighthouses,
  history,
  future,
  completed: isComplete({
    size: state.puzzle.size,
    territories: state.puzzle.territories,
    lighthouses,
  }),
});

export const toggleLighthouse = (state, row, column) => {
  if (state.completed) return state;
  const candidate = { row, column };

  if (hasLighthouse(state.lighthouses, row, column)) {
    if (state.previewedLighthouse?.row !== row || state.previewedLighthouse?.column !== column) {
      return {
        ...state,
        previewedLighthouse: candidate,
        feedback: { type: 'beam-preview', row, column },
      };
    }
    const next = state.lighthouses.filter(
      (lighthouse) => lighthouse.row !== row || lighthouse.column !== column,
    );
    return {
      ...withLighthouses(state, next, [...state.history, snapshot(state)]),
      previewedLighthouse: null,
      beamFlash: null,
      feedback: { type: 'removed', row, column },
    };
  }

  const beams = beamCounts(state.puzzle.size, state.lighthouses);
  const candidateKey = `${row}:${column}`;
  if (beams.has(candidateKey) || conflictingLighthouse(state.lighthouses, candidate)) {
    return {
      ...state,
      strikes: state.strikes + 1,
      feedback: { type: 'geometric-conflict', row, column },
    };
  }

  if (territoryConflict(state.lighthouses, candidate, state.puzzle.territories)) {
    return {
      ...state,
      strikes: state.strikes + 1,
      feedback: { type: 'territory-conflict', row, column },
    };
  }

  const next = [...state.lighthouses, candidate];
  const nextState = withLighthouses(state, next, [...state.history, snapshot(state)]);
  const invalidFullBoard = next.length === state.puzzle.size && !nextState.completed;
  return {
    ...nextState,
    previewedLighthouse: null,
    beamFlash: candidate,
    feedback: invalidFullBoard ? { type: 'invalid-completion' } : { type: 'placed', row, column },
  };
};

export const undo = (state) => {
  if (state.history.length === 0 || state.completed) return state;
  const previous = state.history[state.history.length - 1];
  return {
    ...withLighthouses(
      state,
      previous,
      state.history.slice(0, -1),
      [snapshot(state), ...state.future],
    ),
    previewedLighthouse: null,
    beamFlash: null,
    feedback: { type: 'undo' },
  };
};

export const redo = (state) => {
  if (state.future.length === 0 || state.completed) return state;
  const next = state.future[0];
  return {
    ...withLighthouses(
      state,
      next,
      [...state.history, snapshot(state)],
      state.future.slice(1),
    ),
    previewedLighthouse: null,
    beamFlash: null,
    feedback: { type: 'redo' },
  };
};
